// src/features/fx-ranking/api.ts
//
// Data fetcher for the FX ranking page. Sibling of
// features/commodities-ranking/api.ts. Talks to the FastAPI fx-ranking
// router via the shared `apiClient` axios instance, which:
//   - attaches the WordPress-issued JWT (Authorization: Bearer <token>)
//     so the auth-gated /api/v1/fx-ranking router is reached as an
//     authorized user, and
//   - already bakes the `/api/v1` version prefix into its baseURL.
// We therefore request the bare `/fx-ranking/...` path here.
//
// The FX domain row shape diverges from the commodities contract:
//   `tier` (Tier 1 / Tier 2 / Tier 3) replaces `sector`,
//   `tier_rank` replaces `sector_rank`,
//   the three factor columns retain the same names:
//     - momentum_z_score    -> price action on the USD-numeraire series
//     - fundamental_z_score -> Net Commercial COT positioning (hedgers)
//     - sentiment_z_score   -> Net Non-Commercial COT positioning (specs)
// Only the 11 CFTC-covered G10/EM currencies have COT data; the rest
// return an empty `series` from the /cot/{symbol} endpoint.

import { apiClient } from '../../api/client';

// Matches the exact JSON row shape from GET /api/v1/fx-ranking/top-assets
// and /universe. Byte-identical to FX_TOP_SIGNALS_ROW_KEYS in cache_keys.py.
export interface FXRankingRow {
    symbol: string;
    tier: string;
    forge_ultra_signal: number;
    annualized_volatility: number;
    momentum_z_score: number;
    fundamental_z_score: number;
    sentiment_z_score: number;
    universe_rank: number;
    tier_rank: number;
}

export interface TopAssetsResponse {
    as_of_date: string;
    top_n: number;
    rankings: FXRankingRow[];
}

export const fetchTopAssets = async (): Promise<TopAssetsResponse> => {
    const { data } = await apiClient.get<TopAssetsResponse>(
        '/fx-ranking/top-assets',
    );
    return data;
};

// --- Full universe (30 currencies, non-paginated) -----------------------
// Each universe row additionally carries the curated human-readable `name`.

export interface UniverseRow extends FXRankingRow {
    name: string;
}

export interface UniverseResponse {
    as_of_date: string;
    total: number;
    items: UniverseRow[];
}

export const fetchUniverse = async (
    tier?: string,
): Promise<UniverseResponse> => {
    const { data } = await apiClient.get<UniverseResponse>(
        '/fx-ranking/universe',
        { params: tier ? { tier } : undefined },
    );
    return data;
};

// --- Batched historical trajectories (sparklines) -----------------------

export interface HistoryPoint {
    date: string;
    forge_ultra_signal: number;
}

export type HistoricalBatchResponse = Record<string, HistoryPoint[]>;

export const fetchHistoricalBatch = async (
    symbols: string[],
    days = 90,
): Promise<HistoricalBatchResponse> => {
    if (symbols.length === 0) return {};
    const { data } = await apiClient.get<HistoricalBatchResponse>(
        '/fx-ranking/historical-batch',
        { params: { symbols: symbols.join(','), days } },
    );
    return data;
};

// --- Tier summary (radar chart) -----------------------------------------
// Per-tier average factor z-scores, aggregated server-side over the latest
// date so the client receives three rows (Tier 1 / Tier 2 / Tier 3).

export interface TierSummaryRow {
    tier: string;
    count: number;
    avg_momentum_z: number;
    avg_fundamental_z: number;
    avg_sentiment_z: number;
    avg_signal: number;
}

export interface TierSummaryResponse {
    as_of_date: string;
    tiers: TierSummaryRow[];
}

export const fetchTierSummary = async (): Promise<TierSummaryResponse> => {
    const { data } = await apiClient.get<TierSummaryResponse>(
        '/fx-ranking/tier-summary',
    );
    return data;
};

// --- COT net-positioning history (symbol pop-over) ----------------------
// Weekly Commercial / Non-Commercial net positions for one FX symbol.
// `symbol` is the internal currency code (e.g. JPY, EUR, MXN).
// Returns an empty `series` (not a 404) for currencies without CFTC coverage.

export interface CotPoint {
    date: string;
    net_commercial: number;
    net_noncomm: number;
    commercial_long: number;
    commercial_short: number;
    noncomm_long: number;
    noncomm_short: number;
}

export interface CotHistoryResponse {
    symbol: string;
    cot_symbol: string | null;
    name: string;
    count: number;
    series: CotPoint[];
}

export const fetchCotHistory = async (
    symbol: string,
    days = 365,
): Promise<CotHistoryResponse> => {
    const { data } = await apiClient.get<CotHistoryResponse>(
        `/fx-ranking/cot/${encodeURIComponent(symbol)}`,
        { params: { days } },
    );
    return data;
};

// --- Daily price/volume history (detail pop-over) -----------------------
// USD-numeraire daily OHLCV from GET /api/v1/fx-ranking/price-history/{symbol}.
// Every bar is pre-inverted so it reads "1 unit of <symbol> = X USD".
// `volume` is OTC broker tick-count (informational, frequently null).

export interface PriceBar {
    date: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number | null;
}

export interface PriceHistoryResponse {
    symbol: string;
    name: string;
    count: number;
    series: PriceBar[];
}

export const fetchPriceHistory = async (
    symbol: string,
    days = 365,
): Promise<PriceHistoryResponse> => {
    const { data } = await apiClient.get<PriceHistoryResponse>(
        `/fx-ranking/price-history/${encodeURIComponent(symbol)}`,
        { params: { days } },
    );
    return data;
};

// --- Daily factor z-score history (detail pop-over) ---------------------
// Per-date component z-scores + composite signal from
// GET /api/v1/fx-ranking/factors/{symbol}.

export interface FactorPoint {
    date: string;
    momentum_z_score: number;
    fundamental_z_score: number;
    sentiment_z_score: number;
    annualized_volatility: number;
    forge_ultra_signal: number;
}

export interface FactorsResponse {
    symbol: string;
    name: string;
    count: number;
    series: FactorPoint[];
}

export const fetchFactors = async (
    symbol: string,
    days = 365,
): Promise<FactorsResponse> => {
    const { data } = await apiClient.get<FactorsResponse>(
        `/fx-ranking/factors/${encodeURIComponent(symbol)}`,
        { params: { days } },
    );
    return data;
};
